import React, { useReducer, useEffect } from "react";
import client from "client";
import { useQuery } from "@apollo/client";
import { Redirect } from "react-router-dom";
import { QUERY_PROJECT_CONFIG, QUERY_LIST_TENANTS } from "Queries";
import Loader from "components/Loader/Loader.js";
import { Error } from "components/Utils/Utils";

import { getCookie, setCookie } from "helpers/Cookie";
import { clearSession } from "sagas";
import { cloneDeep } from "lodash/fp";
import * as Sentry from "@sentry/react";
import {
  shouldDisplayProject,
  PROJECTS_WITHOUT_BOARDS_WHITELIST,
} from "../config/projectWhitelist";

const AppContext = React.createContext();
let AppContextConsumer = AppContext.Consumer;

const initialState = {
  config: null,
  currentProject: null,
  tenantID: parseInt(localStorage.defaultTenantID),
  tenantSlug: parseInt(localStorage.defaultTenantSlug),
  projectID: parseInt(localStorage.defaultProjectID),
  loadedConfig: false,
  tenants: [],
  projects: [],
};

let reducer = (state, action) => {
  switch (action.type) {
    case "APP-SET-CONFIG":
      return {
        ...state,
        config: action.config,
        projects: action.config.projects || [],
        version: action.version || localStorage.configVersion || "production",
        loadedConfig: true,
      };
    case "APP-SET-PROJECT":
      return {
        ...state,
        currentProject: action.currentProject,
      };
    case "APP-SET-PROJECT_ID":
      return {
        ...state,
        projectID: action.projectID,
      };
    case "APP-SET-TENANT":
      return {
        config: null,
        currentProject: null,
        projects: [],
        tenantID: action.tenantID,
        tenantSlug: action.tenantSlug,
        projectID: parseInt(localStorage.defaultProjectID),
        version: action.version || localStorage.configVersion || "production",
      };
    case "APP-SET-TENANTS_LISTS":
      return {
        ...state,
        tenants: action.tenants,
      };

    default:
      return state;
  }
};

/**
 * Compares whether there are differences between the current and previous project
 * To be used when the old project should be replaced by the new one
 * @param {*} oldProject Project persisted in the context
 * @param {*} newProject New project calculated
 */
const isProjectDiff = (oldProject, newProject) =>
  oldProject.project !== newProject.project ||
  oldProject.board !== newProject.board ||
  oldProject.page !== newProject.page;

/**
 * Get the current board based on the url
 */
export const getCurrentProjectFromUrl = () => {
  const [project, page] = window.location.pathname.replace("/", "").split("/");
  return {
    project,
    page,
  };
};

/**
 * Get the default project from config if url isn't present
 * It may happen when we land on QB and move back to dashboard
 * @param {*} data
 */
const getDefaultProject = (data) => {
  let projectObj = null;
  let boardObj = null;
  let defaultPageSlug = null;

  let project = null;
  let board = null;
  let page = null;

  let boardSlug = null;

  // Get project - handle case when there are no projects
  if (!data.projects || data.projects.length === 0) {
    return {
      projectObj: null,
      boardObj: null,
      defaultPageSlug: null,
      project: null,
      board: null,
      page: null,
    };
  }

  // Try to find project by defaultProjectID first
  if (localStorage.defaultProjectID) {
    projectObj = data.projects.find(
      (p) => p.id === parseInt(localStorage.defaultProjectID)
    );
  }

  // If no project found by ID or the project doesn't have boards, find first project with boards
  if (!projectObj || !projectObj.boards || projectObj.boards.length === 0) {
    // Filter projects that have boards for the current version
    const currentVersion = localStorage.configVersion || "production";
    const projectsWithBoards = data.projects
      .filter((p) => {
        // For whitelisted projects in production, include them
        const projectId = parseInt(p.id, 10);
        const isWhitelisted =
          PROJECTS_WITHOUT_BOARDS_WHITELIST.includes(projectId);
        const isProduction = currentVersion !== "development";

        if (isWhitelisted && isProduction) {
          return true; // Include whitelisted projects in production
        }

        // For other projects, just check if they should be displayed
        return shouldDisplayProject(p, currentVersion);
      })
      .sort((a, b) => a.position - b.position);

    if (projectsWithBoards.length > 0) {
      projectObj = projectsWithBoards[0];
    } else {
      // Fallback to first project if no projects have boards
      projectObj = data.projects[0] || {};
    }
  }

  // Get page - handle case when there are no boards
  if (projectObj.boards && projectObj.boards.length > 0) {
    [boardObj] = projectObj.boards.filter((b) => b.position === 0);
  }

  // Get the first/default project name
  project = projectObj.slug || null;

  // Get the first/default board
  const filteredBoard = projectObj.boards
    ? projectObj.boards.filter((b) => b.position === 0)
    : [];

  // Get the board's slug
  if (filteredBoard[0] && filteredBoard[0].slug) {
    boardSlug = filteredBoard[0].slug;
  } else {
    boardSlug = null;
  }

  board = boardSlug;
  page = board;

  return {
    projectObj,
    boardObj,
    defaultPageSlug,
    project,
    board,
    page,
  };
};

/**
 * Global context
 * @param {*} props
 */
const AppContextProvider = (props) => {
  let [state, dispatch] = useReducer(reducer, initialState);
  let value = { state, dispatch };
  let data = state.config;

  let defaultPageSlug,
    project,
    board,
    page,
    projectObj,
    boardObj,
    currentProject;

  let [projectName, boardName] = window.location.pathname
    .replace("/", "")
    .split("/");

  defaultPageSlug = null;
  project = null;
  board = null;
  page = null;
  projectObj = null;
  currentProject = null;
  boardObj = null;

  // Set the projectName using the name persisted in the cookie in order to autoload the project selected before
  let pnCookie =
    localStorage.getItem("defaultTenantSlug") ||
    getCookie(getCookie("credential") + "projectName");
  if (projectName === "" && pnCookie !== null) projectName = pnCookie;

  if (data && data.projects.length > 0) {
    // If projectName is empty, it means that we're in the root https://hostname/
    // so, no project was selected and we're going to get the first project from data/config as default
    if (projectName === "") {
      let defaultProject = getDefaultProject(data);

      // We get the first project object
      projectObj = defaultProject.projectObj;
      // Select the page according the position which 0 is the first/default one
      boardObj = defaultProject.boardObj;
      defaultPageSlug = defaultProject.defaultPageSlug;
      project = defaultProject.project;
      board = defaultProject.board;
      page = board;
    } else {
      // If there's a different project selected
      // find the project data according to the slug name having the board name gotten from the url
      projectObj = data.projects.find((p) => p.slug === projectName);

      // There are some Pages that aren't within a board, like Sources
      if (projectObj) {
        // Only try to get board if it exists
        if (projectObj.boards && projectObj.boards.length > 0) {
          [boardObj] = projectObj.boards.filter((b) => b.position === 0);
          defaultPageSlug = boardObj ? boardObj.slug : null;
          board = page = defaultPageSlug;
        } else {
          boardObj = null;
          defaultPageSlug = null;
          board = page = null;
        }
        project = projectName;

        // if a different board was selected
        if (boardName && projectObj.boards) {
          boardObj = projectObj.boards.find((b) => b.slug === boardName);
          page = boardName;
        }
      } else {
        // If the project object wasn't found, it means that we were in a page
        // such QB, Source, Catalogue, etc. These pages aren't set inside the
        // config so, it cannot be found having the URL as reference, so, we set
        // again the default project values into the context
        let defaultProject = getDefaultProject(data);
        project = defaultProject.project;
        projectObj = defaultProject.projectObj;
        boardObj = defaultProject.boardObj;
        board = defaultProject.board;
        page = defaultProject.page;
      }
    }

    // Persist project
    setCookie(getCookie("credential") + "projectName", project, 24 * 60);

    // Set defaultProjectID in localStorage when project is determined
    if (projectObj && projectObj.id) {
      localStorage.setItem("defaultProjectID", projectObj.id);
    }

    currentProject = {
      project,
      projectObj,
      boardObj,
      board,
      page,
      data,
    };
  }

  // Effect to handle project ID dispatch when project changes
  useEffect(() => {
    if (currentProject?.projectObj?.id) {
      dispatch({
        type: "APP-SET-PROJECT_ID",
        projectID: currentProject.projectObj.id,
      });
    }
  }, [currentProject?.projectObj?.id]);

  // Effect to handle initial project ID setting when config is loaded
  useEffect(() => {
    if (
      data &&
      data.projects &&
      data.projects.length > 0 &&
      !localStorage.defaultProjectID
    ) {
      // Find first project with boards for the current version
      const currentVersion = localStorage.configVersion || "production";
      const projectsWithBoards = data.projects
        .filter((p) => {
          // For whitelisted projects in production, include them
          const projectId = parseInt(p.id, 10);
          const isWhitelisted =
            PROJECTS_WITHOUT_BOARDS_WHITELIST.includes(projectId);
          const isProduction = currentVersion !== "development";

          if (isWhitelisted && isProduction) {
            return true; // Include whitelisted projects in production
          }

          // For other projects, just check if they should be displayed
          return shouldDisplayProject(p, currentVersion);
        })
        .sort((a, b) => a.position - b.position);

      const firstProject =
        projectsWithBoards.length > 0
          ? projectsWithBoards[0]
          : data.projects[0];
      if (firstProject && firstProject.id) {
        localStorage.setItem("defaultProjectID", firstProject.id);
        dispatch({
          type: "APP-SET-PROJECT_ID",
          projectID: firstProject.id,
        });
      }
    }
  }, [data, data?.projects]);

  // Falls back to login page when the session has expired
  if (!localStorage.defaultTenantID) {
    clearSession();
    <Redirect
      to={{
        pathname: "/login",
        state: { nextPathname: props.location },
      }}
    />;
  }

  // Load available tenants before running QUERY_PROJECT_CONFIG.
  const { loading: isLoadingTenants } = useQuery(QUERY_LIST_TENANTS, {
    skip: state.tenants && state.tenants.length > 0,
    fetchPolicy: "network-only",
    client: client,
    onCompleted: (qData) => {
      if (qData)
        dispatch({
          type: "APP-SET-TENANTS_LISTS",
          tenants: qData.tenants,
        });
    },
  });

  // Populate default tenant id and slud necessary for QUERY_PROJECT_CONFIG
  if (state.tenants && state.tenants.length > 0) {
    let currentTenant = null;
    currentTenant = state.tenants.find((t) => t.slug === projectName);
    if (currentTenant) {
      localStorage.setItem("defaultTenantID", currentTenant.id);
      localStorage.setItem("defaultTenantSlug", currentTenant.slug);
    }
  }

  const { loading, error } = useQuery(QUERY_PROJECT_CONFIG, {
    // Skip when we're loading tenants or if we already have the config data persisted into the global context
    skip: isLoadingTenants || data,
    fetchPolicy: "network-only",
    client: client,
    variables: {
      version: localStorage.configVersion || "production",
      tenantID: parseInt(localStorage.defaultTenantID),
    },
    onCompleted: (qData) => {
      if (qData) {
        // note: we need to cloneDeep here because props are being set as read-only or not extensible
        const data = cloneDeep({
          ...qData,
          tenants: state.tenants,
        });

        // Initialize defaultProjectID if not set
        if (
          !localStorage.defaultProjectID &&
          data.projects &&
          data.projects.length > 0
        ) {
          // Find first project with boards for the current version
          const currentVersion = localStorage.configVersion || "production";
          const projectsWithBoards = data.projects
            .filter((p) => {
              // For whitelisted projects in production, include them
              const isWhitelisted = PROJECTS_WITHOUT_BOARDS_WHITELIST.includes(
                p.id
              );
              const isProduction = currentVersion !== "development";

              if (isWhitelisted && isProduction) {
                return true; // Include whitelisted projects in production
              }

              // For other projects, just check if they should be displayed
              return shouldDisplayProject(p, currentVersion);
            })
            .sort((a, b) => a.position - b.position);

          const firstProject =
            projectsWithBoards.length > 0
              ? projectsWithBoards[0]
              : data.projects[0];
          if (firstProject && firstProject.id) {
            localStorage.setItem("defaultProjectID", firstProject.id);
          }
        }

        dispatch({
          type: "APP-SET-CONFIG",
          config: data,
          currentProject: currentProject,
          version: localStorage.configVersion || "production",
        });
      }
    },
  });

  if (loading || isLoadingTenants) {
    return <Loader minHeight={500} col={"col-12"} size="circle-scale--sm" />;
  }

  if (error) {
    return (
      <Error
        type="graphql_down"
        minHeight={500}
        report={() => Sentry.showReportDialog()}
        {...props}
      />
    );
  }

  // Updates the context ONLY
  // when the config was already loaded
  // AND the project is being set for the first time
  // OR the project from context must be updated
  if (
    state.config &&
    (!state.currentProject ||
      isProjectDiff(state.currentProject, currentProject))
  ) {
    dispatch({ type: "APP-SET-PROJECT", currentProject });
  }

  return (
    <AppContext.Provider value={value}>{props.children}</AppContext.Provider>
  );
};

export { AppContext, AppContextProvider, AppContextConsumer };
